/**
 * React Context for Articles Components Configuration
 * Provides abstractions for routing, images, and UI components
 */

'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { ArticlesComponentsConfig } from './types';

const ArticlesComponentsContext = createContext<ArticlesComponentsConfig | undefined>(undefined);

export interface ArticlesComponentsProviderProps {
  config: ArticlesComponentsConfig;
  children: ReactNode;
}

/**
 * Provider component that supplies configuration to all child components
 * 
 * @example
 * ```tsx
 * import { ArticlesComponentsProvider } from '@uicwebmaster/articles/config';
 * import Link from 'next/link';
 * import Image from 'next/image';
 * 
 * <ArticlesComponentsProvider
 *   config={{
 *     LinkComponent: Link,
 *     ImageComponent: Image,
 *     baseUrl: '/news/articles',
 *   }}
 * >
 *   <ArticlesList articles={articles} />
 * </ArticlesComponentsProvider>
 * ```
 */
export function ArticlesComponentsProvider({
  config,
  children,
}: ArticlesComponentsProviderProps) {
  return (
    <ArticlesComponentsContext.Provider value={config}>
      {children}
    </ArticlesComponentsContext.Provider>
  );
}

/**
 * Hook to access the articles components configuration
 * Returns default implementations if not provided
 */
export function useArticlesComponents(): Required<ArticlesComponentsConfig> {
  const config = useContext(ArticlesComponentsContext);

  // Default Link component (plain <a> tag)
  const DefaultLink = ({ href, className, children, target, rel, onClick }: any) => (
    <a href={href} className={className} target={target} rel={rel} onClick={onClick}>
      {children}
    </a>
  );

  // Default Image component (plain <img> tag)
  const DefaultImage = ({ src, alt, className, style, width, height }: any) => (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      width={width}
      height={height}
    />
  );

  // Default Button component
  const DefaultButton = ({ className, children, onClick, disabled, type = 'button' }: any) => (
    <button
      type={type}
      className={className}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );

  // Default Pagination component
  const DefaultPagination = ({ currentPage, totalPages, onPageChange }: any) => (
    <div>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          disabled={page === currentPage}
        >
          {page}
        </button>
      ))}
    </div>
  );

  // Default FilterSearch component
  const DefaultFilterSearch = ({ query, onQueryChange, placeholder }: any) => (
    <input
      type="text"
      value={query}
      onChange={e => onQueryChange(e.target.value)}
      placeholder={placeholder}
    />
  );

  // Default FilterSummary component
  const DefaultFilterSummary = ({ totalResults, onClearAll }: any) => (
    <div>
      <span>{totalResults} results</span>
      <button onClick={onClearAll}>Clear all</button>
    </div>
  );

  // Default FilterThemes component
  const DefaultFilterThemes = ({
    themes,
    selectedThemes,
    onToggleTheme,
  }: any) => (
    <div>
      {themes.map((theme: any) => (
        <label key={theme.id}>
          <input
            type="checkbox"
            checked={selectedThemes.includes(theme.id)}
            onChange={() => onToggleTheme(theme.id)}
          />
          {theme.name}
        </label>
      ))}
    </div>
  );

  // Default FilterTags component
  const DefaultFilterTags = ({
    tags,
    selectedTags,
    onToggleTag,
  }: any) => (
    <div>
      {tags.map((tag: any) => (
        <label key={tag.id}>
          <input
            type="checkbox"
            checked={selectedTags.includes(tag.id)}
            onChange={() => onToggleTag(tag.id)}
          />
          {tag.name}
        </label>
      ))}
    </div>
  );

  return {
    LinkComponent: config?.LinkComponent || DefaultLink,
    ImageComponent: config?.ImageComponent || DefaultImage,
    onNavigate: config?.onNavigate || (() => {}),
    Button: config?.Button || DefaultButton,
    Pagination: config?.Pagination || DefaultPagination,
    FilterSearch: config?.FilterSearch || DefaultFilterSearch,
    FilterSummary: config?.FilterSummary || DefaultFilterSummary,
    FilterThemes: config?.FilterThemes || DefaultFilterThemes,
    FilterTags: config?.FilterTags || DefaultFilterTags,
    baseUrl: config?.baseUrl || '/news/articles',
  };
}

